import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Court, useFetchAllCourtsLazyQuery } from '../../../../generated'
import { PAGE_LIMIT } from '../../../constants'

type CourtOption = Pick<Court, 'id' | 'name'>

interface CourtFilterProps {
  courtId: string | undefined
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const CourtFilter: FC<CourtFilterProps> = ({ courtId, setReportFilters }) => {
  const [courts, setCourts] = useState<CourtOption[]>([])
  const [selectedCourt, setSelectedCourt] = useState<CourtOption | null>(null)
  const [courtPage, setCourtPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch] = useDebounce(searchTerm, 500)

  const [fetchCourts, { loading }] = useFetchAllCourtsLazyQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.fetchAllCourts
      if (!result) return
      const { courts: rawCourts, pagination } = result
      const newCourts = (rawCourts || []).filter(Boolean) as CourtOption[]
      const { page: currentPage, totalPages } = pagination || {}
      if (rawCourts) {
        setCourts(prev => (courtPage === 1 ? newCourts : [...prev, ...newCourts]))
        setHasMorePages(currentPage && totalPages ? currentPage < totalPages : false)
      }
    },
    // fetchAllCourts answers a search with no matches with a Not Found error, so treat any
    // error as "no (more) courts" rather than surfacing it.
    onError() {
      if (courtPage === 1) setCourts([])
      setHasMorePages(false)
    }
  })

  useEffect(() => {
    setCourts([])
    setCourtPage(1)
    setHasMorePages(true)
  }, [debouncedSearch])

  useEffect(() => {
    if (!hasMorePages) return
    fetchCourts({
      variables: {
        courtsInput: {
          searchValue: debouncedSearch.trim(),
          paginationOptions: { limit: PAGE_LIMIT, page: courtPage }
        }
      }
    })
  }, [courtPage, debouncedSearch, fetchCourts, hasMorePages])

  // Keep the selected court available in the options list even when it is not in the current search page
  useEffect(() => {
    if (!courtId) {
      setSelectedCourt(null)
      return
    }
    const match = courts.find(court => court.id === courtId)
    if (match) setSelectedCourt(match)
  }, [courtId, courts])

  const options = [
    ...(selectedCourt ? [selectedCourt] : []),
    ...courts.filter(court => court.id !== selectedCourt?.id)
  ]

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const node = event.currentTarget
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 5 && hasMorePages && !loading) {
        setCourtPage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  return (
    <Autocomplete
      options={options}
      value={selectedCourt}
      getOptionLabel={option => option?.name || ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(_, newValue) => {
        setSelectedCourt(newValue)
        setReportFilters(prev => ({ ...prev, courtId: newValue?.id }))
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') setSearchTerm(newInput)
        else if (reason === 'clear') setSearchTerm('')
      }}
      renderInput={params => <TextField {...params} label="Court" />}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' },
        onScroll: handleScroll
      }}
      loading={loading}
      noOptionsText={loading ? 'Loading...' : 'No courts'}
      sx={{ minWidth: 250, maxWidth: 400 }}
    />
  )
}

export default CourtFilter
